import React, { useState, useRef } from 'react';
import useDidUpdateEffect from '@use-effect/did-update';
import { useRouter } from 'next/router';

import SearchInput from 'Elements/Input/Search';
import { SelectInputProps } from 'Elements/Input/Select';

import {
    Filters,
    SearchWrapper,
    RoleSelect,
    DepartmentSelect,
    StyledResetButton
} from './StaffListing.styled';

export interface Props {
    id?: string;
    initialSearchTerm?: string;
    rolesSelectOptions: SelectInputProps['options'];
    departmentsSelectOptions: SelectInputProps['options'];
    initialRoles?: string;
    initialDepartments?: string;
}

const StaffListing: React.FC<Props> = props => {
    const {
        id,
        rolesSelectOptions,
        departmentsSelectOptions,
        initialSearchTerm = '',
        initialRoles = null,
        initialDepartments = null
    } = props;

    const [searchTerm, setSearchTerm] = useState<string>(initialSearchTerm);
    const [activeRoles, setActiveRoles] = useState<string | null>(initialRoles);
    const [activeDepartments, setActiveDepartments] = useState<string | null>(initialDepartments);
    const router = useRouter();
    const timeRef = useRef<null | NodeJS.Timeout>(null);

    /* For the filters */
    useDidUpdateEffect(() => {
        filterResults();
    }, [activeRoles, activeDepartments]);

    /* For the search input */
    useDidUpdateEffect(() => {
        clearTimeout(timeRef.current as NodeJS.Timeout);

        timeRef.current = setTimeout(() => {
            filterResults();
        }, 500);
    }, [searchTerm]);

    const resetForm = () => {
        setSearchTerm('');
        setActiveRoles(null);
        setActiveDepartments(null);
        pushRoute(null);
    };

    const filterResults = () => {
        const query: Record<string, string | number> = {
            page: 1
        };

        if (searchTerm) {
            query.search = searchTerm;
        }

        if (activeRoles) {
            query.role = activeRoles;
        }

        if (activeDepartments) {
            query.department = activeDepartments;
        }

        pushRoute(query);
    };

    // TODO Need to add type for query
    const pushRoute = query => {
        router.push(
            {
                pathname: router.pathname,
                query
            },
            undefined,
            { scroll: false }
        );
    };

    return (
        <form onSubmit={event => event.preventDefault()} onReset={resetForm} id={id}>
            <div role="search">
                <Filters>
                    <SearchWrapper>
                        <SearchInput
                            id="m-staff_profile_search"
                            aria-label="Search by staff name"
                            button={{ ariaLabel: 'Submit search' }}
                            placeholder="Search staff by name..."
                            name="Staff search"
                            defaultValue={searchTerm}
                            handleValueChange={setSearchTerm}
                            handleSubmit={setSearchTerm}
                            variant="button"
                            label="Search"
                        />
                    </SearchWrapper>
                    <RoleSelect
                        id="m-staff_roles_dropdown"
                        label="Roles"
                        defaultValue={activeRoles || ''}
                        placeholder="Roles..."
                        name="Staff roles"
                        handleValueChange={value =>
                            value ? setActiveRoles(value) : setActiveRoles('')
                        }
                        options={rolesSelectOptions}
                    />
                    <DepartmentSelect
                        id="m-staff_departments_dropdown"
                        label="Faculties and Departments"
                        defaultValue={activeDepartments || ''}
                        placeholder="Faculties and Departments..."
                        name="Staff departments"
                        handleValueChange={value =>
                            value ? setActiveDepartments(value) : setActiveDepartments('')
                        }
                        options={departmentsSelectOptions}
                    />
                    <StyledResetButton label="Clear all" ariaLabel="Clear filters" />
                </Filters>
            </div>
        </form>
    );
};

export default StaffListing;
